import React, { useState, useEffect } from 'react';
import { getContacts } from '../../utils/portalStorage';
import { UzairueContact } from '../../data/initialContactsData';
import { getKingdomVillages, KINGDOM_DATA_CHANGE_EVENT } from '../../utils/kingdomDataStorage';

export const BroadcastTool: React.FC = () => {
  const [contacts, setContacts] = useState<UzairueContact[]>([]);
  const [selectedAudience, setSelectedAudience] = useState<string>('all');
  const [selectedVillage, setSelectedVillage] = useState<string>('all');
  const [selectedChapter, setSelectedChapter] = useState<string>('all');
  const [broadcastSubject, setBroadcastSubject] = useState<string>('Official Royal Festival Notice');
  const [broadcastBody, setBroadcastBody] = useState<string>(
    `Peace and royal blessings to all Uzairue indigenes and friends!\n\nYou are cordially invited to participate in the Annual ERUE Festival 2026. Get your Grand Raffle Draw tickets online now at https://erueuzairue.org to win an SUV car, solar power systems, and university scholarships.\n\nKingdom Motto: One People, One Kingdom, One Destiny!`
  );
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [kingdomVillages, setKingdomVillages] = useState(() => getKingdomVillages());

  useEffect(() => {
    setContacts(getContacts());
    const handleUpdate = () => setKingdomVillages(getKingdomVillages());
    window.addEventListener(KINGDOM_DATA_CHANGE_EVENT, handleUpdate);
    return () => window.removeEventListener(KINGDOM_DATA_CHANGE_EVENT, handleUpdate);
  }, []);

  // Filter contacts based on audience selection
  const filteredContacts = contacts.filter((contact) => {
    if (selectedAudience !== 'all' && contact.role !== selectedAudience) return false;
    if (selectedVillage !== 'all' && contact.village !== selectedVillage) return false;
    if (selectedChapter !== 'all' && contact.diasporaChapter !== selectedChapter) return false;
    return true;
  });

  const villages = Array.from(new Set(contacts.map((c) => c.village)));
  const chapters = Array.from(new Set(contacts.map((c) => c.diasporaChapter)));
  const roles = Array.from(new Set(contacts.map((c) => c.role)));

  const formattedMessage = `👑 *UZAIRUE KINGDOM ERUE FESTIVAL 2026* 👑\n📢 *${broadcastSubject.toUpperCase()}*\n\n${broadcastBody}\n\n📍 *Venue:* Ogieneni Palace Grounds, Jattu-Uzairue, Edo State\n🗓️ *Date:* Dec 24–30, 2026\n🌐 *Official Portal:* https://erueuzairue.org\n— *Issued by Uzairue Royal Communication Bureau*`;

  const encodedMessage = encodeURIComponent(formattedMessage);

  // WhatsApp Group Link Generator
  const handleOpenWhatsAppGroup = () => {
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  // SMS Generator
  const handleOpenSMSGroup = () => {
    window.open(`sms:?body=${encodedMessage}`, '_blank');
  };

  // Copy to Clipboard
  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(formattedMessage);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 3000);
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl space-y-8 text-white">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800 pb-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">📢</span>
            <h2 className="text-xl md:text-2xl font-serif font-black text-amber-300">
              WhatsApp & Direct SMS Broadcast Dispatcher
            </h2>
            <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 text-[10px] px-2.5 py-0.5 rounded-full font-bold uppercase">
              Zero Cost • Instant Delivery
            </span>
          </div>
          <p className="text-xs text-slate-300 mt-1">
            Format, preview, and dispatch instant royal broadcasts directly to WhatsApp group chats, broadcast lists, and SMS contacts without external API costs.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyToClipboard}
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-black px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 shadow-md"
          >
            <span>{copySuccess ? '✅ Copied!' : '📋 Copy Broadcast Text'}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Form & Audience Selectors */}
        <div className="lg:col-span-7 space-y-6">
          
          {/* Audience Filter Bar */}
          <div className="bg-slate-950 border border-slate-800 p-5 rounded-2xl space-y-4">
            <h3 className="text-xs font-bold text-amber-400 uppercase tracking-wider flex items-center gap-2">
              <span>🎯</span> Select Target Audience ({filteredContacts.length} Contacts Selected)
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-400 mb-1">Role / Designation</label>
                <select
                  value={selectedAudience}
                  onChange={(e) => setSelectedAudience(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-amber-400 outline-none"
                >
                  <option value="all">All Roles & Leadership</option>
                  {roles.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-400 mb-1">Village / Sub-clan</label>
                <select
                  value={selectedVillage}
                  onChange={(e) => setSelectedVillage(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-amber-400 outline-none"
                >
                  <option value="all">All 22 Villages</option>
                  {kingdomVillages.map((v) => (
                    <option key={v.id} value={v.name}>{v.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-400 mb-1">Diaspora Chapter</label>
                <select
                  value={selectedChapter}
                  onChange={(e) => setSelectedChapter(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-amber-400 outline-none"
                >
                  <option value="all">All Chapters</option>
                  {chapters.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="space-y-2">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">
              Quick Preset Announcements
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setBroadcastSubject('Grand Raffle Draw Launch');
                  setBroadcastBody('The official ERUE Festival Grand Raffle Draw is now open! Buy your ₦1,000 tickets to win an SUV, solar inverter systems, and scholarships. Proceeds fund Uzairue youth empowerment programs.');
                }}
                className="bg-slate-800 hover:bg-slate-700 text-amber-300 text-xs px-3 py-1.5 rounded-lg border border-slate-700 transition-colors"
              >
                🎟️ Raffle Launch
              </button>

              <button
                type="button"
                onClick={() => {
                  setBroadcastSubject('Royal Cultural Parade Notice');
                  setBroadcastBody('Notice to all 22 Village Heads and Cultural Troupe Leaders: Rehearsals for the Grand Masquerade Procession will commence at Jattu Palace Grandstand this Friday by 3:00 PM.');
                }}
                className="bg-slate-800 hover:bg-slate-700 text-emerald-300 text-xs px-3 py-1.5 rounded-lg border border-slate-700 transition-colors"
              >
                🎭 Masquerade Notice
              </button>

              <button
                type="button"
                onClick={() => {
                  setBroadcastSubject('Diaspora Delegates Reception');
                  setBroadcastBody('Special invitation to all arriving international diaspora delegates: Protocol teams will provide complimentary shuttle pickups at Benin Airport. Please confirm arrival details with the secretariat.');
                }}
                className="bg-slate-800 hover:bg-slate-700 text-blue-300 text-xs px-3 py-1.5 rounded-lg border border-slate-700 transition-colors"
              >
                ✈️ Diaspora Airport Pickup
              </button>
            </div>
          </div>

          {/* Broadcast Inputs */}
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1">Broadcast Title / Headline</label>
              <input
                type="text"
                value={broadcastSubject}
                onChange={(e) => setBroadcastSubject(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white focus:border-amber-400 outline-none font-bold"
                placeholder="e.g. Royal Palace Proclamation"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1">Message Body</label>
              <textarea
                value={broadcastBody}
                onChange={(e) => setBroadcastBody(e.target.value)}
                rows={5}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-4 text-xs text-white focus:border-amber-400 outline-none leading-relaxed"
                placeholder="Type your official broadcast message..."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <button
              onClick={handleOpenWhatsAppGroup}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-5 py-3 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center gap-2 shadow-lg"
            >
              <span className="text-base">🟢</span>
              <span>Open in WhatsApp</span>
            </button>

            <button
              onClick={handleOpenSMSGroup}
              className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-5 py-3 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center gap-2 shadow-lg"
            >
              <span className="text-base">📱</span>
              <span>Open in Phone SMS</span>
            </button>
          </div>

        </div>

        {/* Right Column: Realtime Mobile Screen Preview */}
        <div className="lg:col-span-5 bg-slate-950 border border-slate-800 rounded-3xl p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <span className="text-xs font-bold text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
              <span>📲</span> Live WhatsApp Format Preview
            </span>
            <span className="text-[10px] text-slate-500 font-mono">100% Mobile Ready</span>
          </div>

          {/* Phone Frame Mockup */}
          <div className="bg-[#0B141A] rounded-2xl border-4 border-slate-800 p-4 space-y-3 font-sans shadow-inner">
            <div className="bg-[#121B22] p-2.5 rounded-xl border border-slate-800 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-500 text-slate-950 font-black text-xs flex items-center justify-center">
                👑
              </div>
              <div>
                <p className="text-xs font-bold text-slate-200">Uzairue Kingdom Official</p>
                <p className="text-[10px] text-emerald-400 font-mono">Verified Broadcast Channel</p>
              </div>
            </div>

            {/* Chat Bubble */}
            <div className="bg-[#005C4B] text-slate-100 p-4 rounded-xl rounded-tl-none space-y-2 text-xs leading-relaxed border border-emerald-600/30 font-sans shadow-md">
              <p className="font-bold text-amber-300">👑 UZAIRUE KINGDOM ERUE FESTIVAL 2026 👑</p>
              <p className="font-bold text-emerald-100">📢 {broadcastSubject.toUpperCase()}</p>
              <p className="whitespace-pre-wrap text-slate-100 pt-1 text-[11px]">{broadcastBody}</p>

              <div className="border-t border-emerald-500/40 pt-2 text-[10px] text-emerald-200 space-y-0.5 font-mono">
                <p>📍 Venue: Ogieneni Palace Grounds, Jattu</p>
                <p>🗓️ Date: Dec 24–30, 2026</p>
                <p>🌐 Portal: https://erueuzairue.org</p>
                <p className="italic text-[9px] text-emerald-300/80 pt-1">— Issued by Uzairue Royal Secretariat</p>
              </div>
            </div>

            <div className="text-center text-[10px] text-slate-500 font-mono pt-1">
              Recipient count: {filteredContacts.length} Uzairue indigenes
            </div>
          </div>

          {/* Recipient Quick List */}
          <div className="space-y-2 pt-2">
            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
              Selected Recipients Preview ({filteredContacts.length})
            </h4>
            <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
              {filteredContacts.slice(0, 5).map((c) => (
                <div key={c.id} className="p-2 bg-slate-900 rounded-lg text-[11px] flex items-center justify-between border border-slate-800">
                  <div>
                    <span className="font-bold text-slate-200">{c.fullName}</span>
                    <span className="text-[10px] text-slate-400 block">{c.role} • {c.village}</span>
                  </div>
                  <a
                    href={`https://wa.me/${c.phone.replace(/[^0-9]/g, '')}?text=${encodedMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] bg-emerald-950 text-emerald-300 hover:bg-emerald-800 px-2 py-1 rounded font-bold border border-emerald-700/50"
                  >
                    Send Direct
                  </a>
                </div>
              ))}
              {filteredContacts.length > 5 && (
                <p className="text-[10px] text-slate-500 text-center italic py-1">
                  + {filteredContacts.length - 5} more contacts
                </p>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BroadcastTool;
